import { type CSSProperties, type ReactNode } from 'react';
import type { MenuItem, MenuVariant } from './Menu';

export const isPlainPrimaryClick = (event: MouseEvent) => {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
};

const resolveVisibility = (visible?: boolean | (() => boolean)) => {
  if (typeof visible === 'function') {
    return visible();
  }

  if (typeof visible === 'boolean') {
    return visible;
  }

  return true;
};

export const filterVisibleItems = (items: MenuItem[]): MenuItem[] => {
  return items
    .filter(item => resolveVisibility(item.visible))
    .map(item => ({
      ...item,
      children: item.children ? filterVisibleItems(item.children) : [],
    }));
};

export const applyTypoStyles = (item: HTMLElement) => {
  const typo = item.shadowRoot?.querySelector('bds-typo') as HTMLElement;
  if (!typo) return;

  const { fontSize, lineHeight } = item.style;
  if (fontSize) typo.style.setProperty('font-size', fontSize, 'important');
  if (lineHeight) typo.style.setProperty('line-height', lineHeight, 'important');

  const textEl = typo.shadowRoot?.querySelector('span, div, p, .text, slot') as HTMLElement;
  if (textEl) {
    textEl.style.setProperty('font-size', 'inherit', 'important');
    textEl.style.setProperty('line-height', 'inherit', 'important');
  }
};

const injectStyleToShadow = (shadowRoot: ShadowRoot, id: string, css: string) => {
  if (shadowRoot.querySelector(`#${id}`)) return;
  const style = document.createElement('style');
  style.id = id;
  style.innerHTML = css;
  shadowRoot.appendChild(style);
};

export const applyThirdLevelStyles = (item: HTMLElement) => {
  if (!item.classList.contains('menu__item--third-level') || !item.shadowRoot) return;

  const itemBtn = item.shadowRoot.querySelector('.nav_tree_item, .nav_tree_item_button') as HTMLElement;
  if (itemBtn) {
    itemBtn.style.setProperty('width', '200px', 'important');
    itemBtn.style.setProperty('margin', '0 auto', 'important');
    itemBtn.style.setProperty('box-sizing', 'border-box', 'important');
    itemBtn.style.setProperty('padding-left', '24px', 'important');
    itemBtn.style.setProperty('padding-right', '8px', 'important');
    itemBtn.style.setProperty('border-radius', '8px', 'important');
  }

  injectStyleToShadow(
    item.shadowRoot,
    'third-level-bullet',
    `
    .nav_tree_item_content { position: relative !important; }
    .nav_tree_item_content::before {
      content: ''; display: block !important; width: 6px !important; height: 6px !important;
      background-color: var(--color-content-default, currentColor) !important;
      border-radius: 50% !important; position: absolute !important;
      left: -16px !important; top: 50% !important; transform: translateY(-50%) !important;
      z-index: 10 !important;
    }
    `,
  );
};

export const findPathToActiveItem = (items: MenuItem[], targetPath: string): string[] | null => {
  const normalizePath = (path?: string) => {
    if (!path) return '';
    const trimmedPath = path.trim();
    if (!trimmedPath || trimmedPath === '/') return '/';

    let end = trimmedPath.length;
    while (end > 1 && trimmedPath[end - 1] === '/') {
      end -= 1;
    }

    return trimmedPath.slice(0, end);
  };

  const normalizedTargetPath = normalizePath(targetPath);

  for (const item of items) {
    if (normalizePath(item.path) === normalizedTargetPath) {
      return [item.id];
    }
    if (item.children && item.children.length > 0) {
      const childPath = findPathToActiveItem(item.children, normalizedTargetPath);
      if (childPath) {
        return [item.id, ...childPath];
      }
    }
  }
  return null;
};

const findItemById = (list: MenuItem[], idToken?: string): MenuItem | undefined => {
  if (!idToken) {
    return undefined;
  }

  for (const item of list) {
    if (item.id === idToken) {
      return item;
    }

    if (item.children) {
      const found = findItemById(item.children, idToken);
      if (found) {
        return found;
      }
    }
  }

  return undefined;
};

const resolveItemDataFromElement = (el: Element, visibleItems: MenuItem[]): MenuItem | null => {
  const htmlEl = el as HTMLElement;
  const elementWithToken = htmlEl.dataset.test
    ? htmlEl
    : el.querySelector<HTMLElement>('[data-test^="menu-item-"]');
  const idToken = elementWithToken?.dataset.test?.replaceAll('menu-item-', '');

  return findItemById(visibleItems, idToken) ?? null;
};

const shouldSkipInnerNavItemClick = (el: Element) => {
  const isInnerNavItem = el.classList.contains('menu__item');
  return isInnerNavItem && Boolean(el.closest('.menu__item-link, .menu__item-wrapper'));
};

const createMenuItemClickHandler = (
  el: Element,
  itemData: MenuItem | null,
  handleItemClick: (item: MenuItem) => void,
) => {
  return (event: Event) => {
    if (!itemData) return;

    const mouseEvent = event as MouseEvent;

    if (shouldSkipInnerNavItemClick(el)) {
      return;
    }

    const isLinkWrapper = el.classList.contains('menu__item-link');
    if (isLinkWrapper && !isPlainPrimaryClick(mouseEvent)) {
      return;
    }

    if (isLinkWrapper && itemData.target === '_blank') {
      return;
    }

    handleItemClick(itemData);
  };
};

const createMenuItemTooltipHandler = (el: Element, positionTooltip: (event: any) => void) => {
  return (event: Event) => {
    if (el.classList.contains('menu__item-wrapper')) {
      positionTooltip.call(el, event);
    }
  };
};

const createMenuItemKeyDownHandler = (el: Element) => {
  return (event: Event) => {
    const keyboardEvent = event as KeyboardEvent;
    if (el.classList.contains('menu__item-wrapper') && (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ')) {
      keyboardEvent.preventDefault();
    }
  };
};

export const bindMenuItemEvents = (
  el: Element,
  visibleItems: MenuItem[],
  handleItemClick: (item: MenuItem) => void,
  positionTooltip: (event: any) => void,
) => {
  const itemData = resolveItemDataFromElement(el, visibleItems);
  const clickHandler = createMenuItemClickHandler(el, itemData, handleItemClick);
  const tooltipHandler = createMenuItemTooltipHandler(el, positionTooltip);
  const keyDownHandler = createMenuItemKeyDownHandler(el);

  el.addEventListener('click', clickHandler);

  const isWrapper = el.classList.contains('menu__item-wrapper');
  if (isWrapper) {
    el.addEventListener('mouseenter', tooltipHandler);
    el.addEventListener('focus', tooltipHandler);
    el.addEventListener('keydown', keyDownHandler);
  }

  return () => {
    el.removeEventListener('click', clickHandler);
    el.removeEventListener('mouseenter', tooltipHandler);
    el.removeEventListener('focus', tooltipHandler);
    el.removeEventListener('keydown', keyDownHandler);
  };
};

export const applyContainerAlignment = (item: HTMLElement) => {
  const shadowRoot = item.shadowRoot;
  if (!shadowRoot) return;

  const container = shadowRoot.querySelector('.accordion .container, .container') as HTMLElement;
  if (container) {
    container.style.setProperty('padding-left', '0', 'important');
    container.style.setProperty('padding-right', '0', 'important');

    injectStyleToShadow(
      shadowRoot,
      'remove-timeline',
      `
      .container::before, .accordion .container::before { display: none !important; }
      .accordion, .accordion-item { overflow: visible !important; }
      `,
    );
  }
};

const METRICS_ITEM_STYLE: CSSProperties = {
  width: '224px',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '22px',
  whiteSpace: 'nowrap',
};

const METRICS_NESTED_ITEM_STYLE: CSSProperties = {
  ...METRICS_ITEM_STYLE,
  fontSize: '12px',
  lineHeight: '20px',
};

export const getMenuItemStyleByLevel = (level: number, variant: MenuVariant = 'default'): CSSProperties => {
  if (variant === 'metrics') {
    return level === 0 ? METRICS_ITEM_STYLE : METRICS_NESTED_ITEM_STYLE;
  }

  if (level === 0) {
    return {
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '24px',
      whiteSpace: 'nowrap',
    };
  }

  if (level === 1) {
    return {
      left: '8px',
      width: '218px',
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '22px',
      whiteSpace: 'nowrap',
    };
  }

  if (level === 2) {
    return {
      left: '8px',
      fontWeight: 400,
      fontSize: '12px',
      lineHeight: '20px',
      whiteSpace: 'nowrap',
    };
  }

  return { whiteSpace: 'nowrap' };
};

export const getMenuItemClass = (
  item: MenuItem,
  level: number,
  variant: MenuVariant = 'default',
) => {
  const disabledClass = item.disabled ? ' menu__item--disabled' : '';
  if (variant === 'metrics') {
    const bulletClass = level > 0 ? ' menu__item--bulleted' : '';
    return `menu__item${disabledClass}${bulletClass}`;
  }

  const thirdLevelClass = level === 2 ? ' menu__item--third-level' : '';
  return `menu__item${disabledClass}${thirdLevelClass}`;
};

export const getOpenProps = (item: MenuItem, variant: MenuVariant): Record<string, string> => {
  const hasChildren = (item.children?.length ?? 0) > 0;
  if (variant !== 'metrics' || !hasChildren || item.defaultOpen === false) {
    return {};
  }

  return { 'is-open': 'true' };
};

export const applyMetricsStyles = (item: HTMLElement) => {
  const shadowRoot = item.shadowRoot;
  if (!shadowRoot) return;

  injectStyleToShadow(
    shadowRoot,
    'metrics-item-style',
    `
    .nav_tree_item_button, .nav_tree_item { border-radius: 8px !important; }
    :host(.menu__item--bulleted) .nav_tree_item_content { position: relative !important; }
    :host(.menu__item--bulleted) .nav_tree_item_content::before {
      content: ''; display: block !important; width: 6px !important; height: 6px !important;
      background-color: var(--color-content-default, currentColor) !important; border-radius: 50% !important;
      position: absolute !important; left: -16px !important; top: 50% !important;
      transform: translateY(-50%) !important;
    }
    :host(.menu__item--bulleted) .nav_tree_item_content:hover::before,
    :host(.menu__item--bulleted:hover) .nav_tree_item_content::before {
      background-color: var(--color-content-default, currentColor) !important;
    }
    :host([data-active='true']) .nav_tree_item_button,
    :host([data-active='true']) .nav_tree_item {
      background: var(--color-surface-3, #2b2b32) !important; font-weight: 700 !important;
    }
    `,
  );

  const itemBtn = shadowRoot.querySelector<HTMLElement>('.nav_tree_item, .nav_tree_item_button');
  if (item.classList.contains('menu__item--bulleted')) {
    itemBtn?.style.setProperty('padding-left', '24px', 'important');
  }
};

export const getTooltipText = (item: MenuItem, menuLabels: any): string | undefined => {
  if (!item.disabled || !item.disabledReason) {
    return undefined;
  }

  return item.disabledReason === 'upgrade'
    ? menuLabels?.menuDisabledUpgrade
    : menuLabels?.menuDisabledPermission;
};

export const wrapMenuItemWithTooltip = (item: MenuItem, navItem: ReactNode, tooltipText: string) => {
  return (
    <button
      className="menu__item-wrapper"
      key={item.id}
      type="button"
      aria-label={item.label}
      data-test={`menu-item-${item.id}`}
    >
      {navItem}
      <div className="menu__tooltip-container">
        <div className="menu__tooltip">
          {tooltipText}
        </div>
      </div>
    </button>
  );
};

export const wrapMenuItemWithLink = (item: MenuItem, navItem: ReactNode) => {
  return (
    <a
      className="menu__item-link"
      key={item.id}
      href={item.href}
      aria-label={item.label}
      target={item.target === '_blank' ? '_blank' : '_self'}
      rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
    >
      {navItem}
    </a>
  );
};
